import type {
  JsonObject,
  ObservationDataset,
  RuntimeCommand,
  RuntimeErrorInfo,
  RuntimeSessionSnapshot,
} from '../contracts/index.ts';
import type { ObservationStore } from './ObservationStore.ts';
import type { RuntimeWorkerTransport } from './RuntimeExecutionAdapter.ts';
import type {
  RuntimeSession,
  RuntimeSessionListener,
} from './RuntimeSession.ts';
import {
  runtimeCommandSchema,
  runtimeSessionSnapshotSchema,
} from '../contracts/index.ts';
import { WorkerRuntimeExecutionAdapter } from './RuntimeExecutionAdapter.ts';

export interface WorkerBackedRuntimeSessionInput {
  id: string;
  capabilityKey: string;
  config: JsonObject;
  seed: number;
  initialParameters: JsonObject;
  observations: ObservationStore;
  worker: RuntimeWorkerTransport;
  requestTimeoutMs?: number;
}

export class WorkerBackedRuntimeSession implements RuntimeSession {
  readonly id: string;
  readonly capabilityKey: string;
  private readonly adapter: WorkerRuntimeExecutionAdapter;
  private readonly listeners = new Set<RuntimeSessionListener>();
  private readonly workerDatasetIds = new Set<string>();
  private readonly workerDatasetRevisions = new Map<string, number>();
  private readonly ready: Promise<void>;
  private queue: Promise<void> = Promise.resolve();
  private snapshot: RuntimeSessionSnapshot;
  private initializationError: Error | null = null;
  private disposed = false;

  constructor(private readonly input: WorkerBackedRuntimeSessionInput) {
    this.id = input.id;
    this.capabilityKey = input.capabilityKey;
    this.snapshot = runtimeSessionSnapshotSchema.parse({
      id: input.id,
      capabilityKey: input.capabilityKey,
      status: 'starting',
      elapsedTime: 0,
      speed: 1,
      parameters: input.initialParameters,
      state: {},
      error: null,
      execution: 'worker',
    });
    this.adapter = new WorkerRuntimeExecutionAdapter(
      input.worker,
      input.id,
      input.requestTimeoutMs,
      datasets => this.syncDatasets(datasets),
    );
    this.adapter.subscribe((snapshot) => {
      this.snapshot = {
        ...structuredClone(snapshot),
        execution: 'worker',
      };
      this.emit();
    });
    this.ready = this.adapter.initialize({
      capabilityKey: input.capabilityKey,
      config: input.config,
      seed: input.seed,
    }).then((snapshot) => {
      if (this.disposed)
        return;
      this.snapshot = {
        ...structuredClone(snapshot),
        execution: 'worker',
      };
      this.emit();
    }).catch((error) => {
      if (this.disposed)
        return;
      this.initializationError = normalizeError(error);
      this.snapshot = {
        ...this.snapshot,
        status: 'error',
        error: runtimeError(
          this.initializationError,
          'worker.initialize',
          false,
        ),
      };
      this.emit();
    });
  }

  start(): Promise<void> {
    return this.dispatch({ type: 'start' });
  }

  pause(): Promise<void> {
    return this.dispatch({ type: 'pause' });
  }

  resume(): Promise<void> {
    return this.dispatch({ type: 'resume' });
  }

  stop(): Promise<void> {
    return this.dispatch({ type: 'stop' });
  }

  reset(): Promise<void> {
    return this.dispatch({ type: 'reset' });
  }

  step(steps = 1): Promise<void> {
    return this.dispatch({ type: 'step', steps });
  }

  setSpeed(speed: number): void {
    void this.dispatch({ type: 'speed.set', speed }).catch(() => undefined);
  }

  updateParameters(parameters: JsonObject): Promise<void> {
    return this.dispatch({ type: 'parameters.patch', parameters });
  }

  dispatch(rawCommand: RuntimeCommand): Promise<void> {
    const command = runtimeCommandSchema.parse(rawCommand);
    this.assertUsable();
    const operation = this.queue
      .catch(() => undefined)
      .then(async () => {
        await this.ready;
        if (this.initializationError)
          throw this.initializationError;
        this.assertUsable();
        await this.adapter.dispatch(command);
      });
    this.queue = operation;
    return operation;
  }

  getSnapshot(): RuntimeSessionSnapshot {
    return structuredClone(this.snapshot);
  }

  subscribe(listener: RuntimeSessionListener): () => void {
    this.assertUsable();
    this.listeners.add(listener);
    listener(this.getSnapshot());
    return () => this.listeners.delete(listener);
  }

  dispose(): void {
    if (this.disposed)
      return;
    this.disposed = true;
    this.adapter.dispose();
    this.snapshot = {
      ...this.snapshot,
      status: 'disposed',
      error: null,
    };
    this.emit();
    for (const datasetId of this.workerDatasetIds)
      this.input.observations.remove(datasetId);
    this.workerDatasetIds.clear();
    this.workerDatasetRevisions.clear();
    this.listeners.clear();
  }

  private syncDatasets(datasets: ObservationDataset[]): void {
    if (this.disposed)
      return;
    const incomingIds = new Set(datasets.map(dataset => dataset.id));
    const nextWorkerRevisions = new Map<string, number>();
    const updates = datasets.flatMap((dataset) => {
      const previousWorkerRevision = this.workerDatasetRevisions.get(
        dataset.id,
      );
      nextWorkerRevisions.set(dataset.id, dataset.revision);
      if (previousWorkerRevision === dataset.revision)
        return [];
      const localRevision = this.input.observations.getRevision(dataset.id);
      const revision = localRevision === undefined
        ? dataset.revision
        : Math.max(dataset.revision, localRevision + 1);
      return [{
        ...dataset,
        revision,
        updateMode: 'replace' as const,
        removedRowIds: [],
      }];
    });
    if (updates.length > 0)
      this.input.observations.applyBatch(updates);
    for (const datasetId of this.workerDatasetIds) {
      if (!incomingIds.has(datasetId))
        this.input.observations.remove(datasetId);
    }
    this.workerDatasetIds.clear();
    this.workerDatasetRevisions.clear();
    for (const datasetId of incomingIds) {
      this.workerDatasetIds.add(datasetId);
      this.workerDatasetRevisions.set(
        datasetId,
        nextWorkerRevisions.get(datasetId)!,
      );
    }
  }

  private emit(): void {
    const snapshot = this.getSnapshot();
    for (const listener of this.listeners) {
      try {
        listener(snapshot);
      }
      catch {
        // A UI listener cannot interrupt the worker session lifecycle.
      }
    }
  }

  private assertUsable(): void {
    if (this.disposed)
      throw new Error('Worker 运行会话已经释放');
  }
}

function normalizeError(error: unknown): Error {
  return error instanceof Error ? error : new Error('Worker 初始化失败');
}

function runtimeError(
  error: Error,
  operation: string,
  recoverable: boolean,
): RuntimeErrorInfo {
  return {
    code: error.name || 'WorkerRuntimeError',
    message: error.message,
    operation,
    recoverable,
    occurredAt: new Date().toISOString(),
  };
}
